import { isEmpty } from 'lodash';

import { REQUEST_METHODS } from 'constants/api';
import { API_TAG_TYPES } from 'constants/apiTagTypes';
import { PROJECT_TYPES } from 'constants/commonProjects';
import { ORGANISATION_USER_STATUS, ORGANISATIONS_STATUSES } from 'constants/organisations';
import { USER_PERMISSIONS } from 'constants/userPermissions';
import { downloadFile } from 'helper/downloadFile';
import { formatOrganisationData } from 'helper/formatOrganisationData';
import { sortOrganisations } from 'helper/sortOrganisations';
import { formatActivityLogs } from 'helper/formatActivityLogs';
import { getRestoredItemTags } from 'helper/getRestoredItemTags';
import { baseApi } from 'store/baseApi';

export const organisationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOrganisations: build.query({
      query: (params) => ({
        url: '/organisations/',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.ORGANISATIONS, id })),
              { type: API_TAG_TYPES.ORGANISATIONS, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.ORGANISATIONS, id: 'LIST' }],
      transformResponse: (baseQueryReturnValue = []) =>
        sortOrganisations(baseQueryReturnValue.map((organisation) => formatOrganisationData(organisation))),
    }),
    getOrganisationById: build.query({
      query: ({ organisationId }) => ({ url: `/organisations/${organisationId}/` }),
      providesTags: (result, _error, { organisationId }) => [
        { type: API_TAG_TYPES.ORGANISATIONS, id: result?.id ?? organisationId },
      ],
      transformResponse: (baseQueryReturnValue) => formatOrganisationData(baseQueryReturnValue),
    }),
    getOrganisationProjects: build.query({
      query: ({ organisationId, params }) => ({
        url: `/organisations/${organisationId}/project-list/`,
        params,
      }),
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map(({ id }) => ({ type: API_TAG_TYPES.ORGANISATION_PROJECTS, id })),
              { type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.ORGANISATION_PROJECTS, id: 'LIST' }],
      transformResponse: (baseQueryReturnValue) => {
        if (isEmpty(baseQueryReturnValue)) {
          return [];
        }

        const {
          organisation_id: organisationId,
          feedback_frameworks: feedbackFrameworks = [],
          projects = [],
        } = baseQueryReturnValue;

        return [
          ...projects.map((item) => ({
            organisationId,
            id: item.id,
            name: item.name,
            projectType: PROJECT_TYPES.CULTURE_PROJECT,
            status: item.status,
            currentUserRoles: item.current_user_roles ?? null,
            datasetsCount: item.datasets_count ?? null,
            isSubmitted: item.is_submitted,
            journalId: item.journal_id,
            launchDate: new Date(item.launch_date).getTime(),
          })),
          ...feedbackFrameworks.map((item) => ({
            organisationId,
            id: item.pk,
            name: item.name,
            projectType: PROJECT_TYPES.FEEDBACK_FRAMEWORK,
            status: item.status,
            reportsCount: item.current_user_reports_number ?? null,
            personalJournalId: item.personal_journal_id ?? null,
            selfReflectionNominee: item.self_reflection_nominee ?? null,
            nomineesSelected: item.nominees_selected ?? null,
            isSubmitted: item.is_submitted,
            launchDate: new Date(item.start_date).getTime(),
          })),
        ].toSorted((a, b) => b.launchDate - a.launchDate);
      },
    }),
    addOrganisation: build.mutation({
      query: (data) => ({
        url: '/organisations/',
        body: data,
        method: REQUEST_METHODS.POST,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.ORGANISATIONS, id: 'LIST' }],
    }),
    updateOrganisationStatus: build.mutation({
      query: ({ organisationId, data }) => ({
        url: `/organisations/${organisationId}/update-status/`,
        body: data,
        method: REQUEST_METHODS.PATCH,
      }),
      invalidatesTags: (_result, _error, { organisationId: id, data }) =>
        data.status === ORGANISATIONS_STATUSES.DELETED
          ? [
              { type: API_TAG_TYPES.ORGANISATIONS, id: 'LIST' },
              { type: API_TAG_TYPES.ACTIVITY_LOGS, id: 'LIST' },
            ]
          : [{ type: API_TAG_TYPES.ORGANISATIONS, id }],
    }),
    renameOrganisation: build.mutation({
      query: ({ organisationId, data }) => ({
        url: `/organisations/${organisationId}/update-name/`,
        body: data,
        method: REQUEST_METHODS.PATCH,
      }),
      invalidatesTags: (_result, _error, { organisationId: id }) => [{ type: API_TAG_TYPES.ORGANISATIONS, id }],
    }),
    getOrganisationUsers: build.query({
      query: ({ organisationId, params }) => ({
        url: `/organisations/${organisationId}/users/`,
        params,
      }),
      providesTags: [{ type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' }],
      transformResponse: ({ results = [], count, next }) => ({
        count,
        next,
        users: results.map(({ user, ...info }) => ({
          ...info,
          ...user,
          user_type: info.is_organisation_admin ? USER_PERMISSIONS.ORGANISATION_ADMIN : USER_PERMISSIONS.USER,
          user_status: info.is_active ? ORGANISATION_USER_STATUS.ACTIVE : ORGANISATION_USER_STATUS.INACTIVE,
        })),
      }),
    }),
    changeUsersPermissions: build.mutation({
      query: ({ data, organisationId }) => ({
        url: `/organisations/${organisationId}/users/change-permission/`,
        body: data,
        method: REQUEST_METHODS.PATCH,
      }),
      invalidatesTags: [
        { type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' },
        { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
      ],
    }),
    deleteOrganisationsUsers: build.mutation({
      query: ({ data, organisationId }) => ({
        url: `/organisations/${organisationId}/users/remove/`,
        body: data,
        method: REQUEST_METHODS.DELETE,
      }),
      invalidatesTags: (_result, error) =>
        !error
          ? [
              { type: API_TAG_TYPES.ORGANISATION_USERS, id: 'LIST' },
              { type: API_TAG_TYPES.PROJECT_USERS, id: 'LIST' },
              { type: API_TAG_TYPES.FRAMEWORK_USERS, id: 'LIST' },
              { type: API_TAG_TYPES.ORGANISATION_ACTIVITY_LOGS, id: 'LIST' },
              { type: API_TAG_TYPES.ORGANISATION_PORTFOLIO },
              { type: API_TAG_TYPES.PROJECTS_JOURNALS, id: 'LIST' },
              { type: API_TAG_TYPES.ACTION_OWNERS, id: 'LIST' },
            ]
          : [],
    }),
    downloadOrganisationUsersCsv: build.mutation({
      queryFn: async ({ organisationId, fileName }, api, extraOptions, baseQueryFn) => {
        const result = await baseQueryFn({
          url: `/organisations/${organisationId}/users/download-csv/`,
          responseHandler: (response) => response.blob(),
        });

        downloadFile(result.data, fileName);

        return { data: null };
      },
    }),
    getOrganisationSecuritySettings: build.query({
      query: ({ organisationId }) => ({ url: `/organisations/${organisationId}/security-settings/` }),
      providesTags: [{ type: API_TAG_TYPES.SECURITY_SETTINGS }],
      transformResponse: (baseQueryReturnValue = {}) => ({
        isMFAEnabled: baseQueryReturnValue?.is_mfa_enabled ?? false,
        isSSOEnabled: baseQueryReturnValue?.is_sso_enabled ?? false,
        ssoMetadataUrl: baseQueryReturnValue?.sso_metadata_url ?? '',
        ssoEntityId: baseQueryReturnValue?.sso_entity_id ?? '',
        ssoReplyUrl: baseQueryReturnValue?.sso_reply_url ?? '',
      }),
    }),
    updateOrganisationSecuritySettings: build.mutation({
      query: ({ organisationId, data }) => ({
        url: `/organisations/${organisationId}/security-settings/`,
        body: data,
        method: REQUEST_METHODS.PATCH,
      }),
      invalidatesTags: [{ type: API_TAG_TYPES.SECURITY_SETTINGS }],
    }),
    getOrganisationActivityLogs: build.query({
      query: ({ organisationId, params }) => ({
        url: `/organisations/${organisationId}/activity-logs/`,
        params,
      }),
      providesTags: [{ type: API_TAG_TYPES.ORGANISATION_ACTIVITY_LOGS, id: 'LIST' }],
      transformResponse: (baseQueryReturnValue = {}) => formatActivityLogs(baseQueryReturnValue),
    }),
    restoreItemFromOrganisationActivityLog: build.mutation({
      query: ({ organisationId, logId }) => ({
        url: `/organisations/${organisationId}/activity-logs/${logId}/restore/`,
        method: REQUEST_METHODS.POST,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAG_TYPES.ORGANISATION_ACTIVITY_LOGS, id: 'LIST' },
              ...getRestoredItemTags({
                id: result.id,
                category: result.category,
              }),
            ]
          : [],
    }),
  }),
});

export const {
  useGetOrganisationsQuery,
  useGetOrganisationByIdQuery,
  useGetOrganisationProjectsQuery,
  useAddOrganisationMutation,
  useUpdateOrganisationStatusMutation,
  useRenameOrganisationMutation,
  useGetOrganisationUsersQuery,
  useChangeUsersPermissionsMutation,
  useDeleteOrganisationsUsersMutation,
  useDownloadOrganisationUsersCsvMutation,
  useGetOrganisationSecuritySettingsQuery,
  useUpdateOrganisationSecuritySettingsMutation,
  useGetOrganisationActivityLogsQuery,
  useRestoreItemFromOrganisationActivityLogMutation,
} = organisationsApi;
